import { notFound } from "@tanstack/solid-router";
import { createServerFn } from "@tanstack/solid-start";
import { z } from "zod";
import { withSentrySpan } from "~/lib/sentry-span";
import { authFunctionMiddleware } from "~/server/middleware/auth";

export const GetBankAccountDetailPageDataInputSchema = z.object({
  businessId: z.string().trim().min(1),
  bankAccountId: z.string().trim().min(1),
});

/**
 * Aggregate server function for the bank account detail page.
 *
 * Fetches business detail and bank account detail in a single HTTP round trip
 * on client-side navigation.
 */
export const getBankAccountDetailPageData = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => GetBankAccountDetailPageDataInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("bankAccountDetail.pageData", async () => {
      const [businessMod, bankAccountMod] = await Promise.all([
        import("../business/business.server"),
        import("./bank-account.server"),
      ]);
      const { getBusinessDetailForUser } = businessMod;
      const { getBankAccountForUser } = bankAccountMod;

      const [businessDetail, bankAccount] = await Promise.all([
        getBusinessDetailForUser(context.user.id, data.businessId),
        getBankAccountForUser(
          context.user.id,
          data.businessId,
          data.bankAccountId,
        ),
      ]);

      if (!bankAccount && data.bankAccountId !== "new") {
        // notFound() (not a bare Response) — thrown Responses can't be
        // serialized by the server RPC layer (Seroval crash → 500) and aren't
        // rejected properly on the client. notFound() renders the route's
        // notFoundComponent with a clean 404.
        throw notFound();
      }

      return { businessDetail, bankAccount } as const;
    });
  });
